import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { LoadingOverlay } from "../components/LoadingOverlay";
import { OfferPdfViewer } from "../components/OfferPdfViewer";
import { SignaturePad } from "../components/SignaturePad";

const API_BASE = (process.env.REACT_APP_API_URL || "http://localhost:30801");

async function fetchView(token: string) {
  const r = await fetch(`${API_BASE}/api/public/esign/${token}`);
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  return r.json();
}

async function respond(
  token: string, action: "sign" | "decline",
  typedName: string, signatureDrawn: string | null, consentChecked: boolean, reason: string,
) {
  const path = action === "sign" ? "sign" : "decline";
  const body = action === "sign"
    ? { typed_name: typedName, signature_drawn: signatureDrawn, consent_checked: consentChecked }
    : { reason };
  const r = await fetch(`${API_BASE}/api/public/esign/${token}/${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error((err as any).detail || "Failed to respond");
  }
  return r.json();
}

/** Public (no-account) recipient signing page — the target of the magic
 * link emailed to external recipients. Mirrors OfferAcceptPage's structure. */
export default function PublicEsignSignPage() {
  const { token } = useParams<{ token: string }>();
  const [responding, setResponding] = useState<"sign" | "decline" | null>(null);
  const [responded, setResponded] = useState<"signed" | "declined" | null>(null);
  const [error, setError] = useState("");
  const [readToEnd, setReadToEnd] = useState(false);
  const [typedName, setTypedName] = useState("");
  const [signatureDrawn, setSignatureDrawn] = useState<string | null>(null);
  const [consentChecked, setConsentChecked] = useState(false);
  const [declining, setDeclining] = useState(false);
  const [declineReason, setDeclineReason] = useState("");

  const { data: rawData, isLoading, isError } = useQuery({
    queryKey: ["publicEsign", token],
    queryFn: () => fetchView(token!),
    enabled: !!token,
    retry: 1,
  });
  const d = rawData as any;
  const pdfUrl = `${API_BASE}/api/public/esign/${token}/pdf`;

  async function handle(action: "sign" | "decline") {
    setResponding(action);
    setError("");
    try {
      await respond(token!, action, typedName.trim(), signatureDrawn, consentChecked, declineReason.trim());
      setResponded(action === "sign" ? "signed" : "declined");
    } catch (e: any) {
      setError(e.message || "Something went wrong. Please try again.");
    } finally {
      setResponding(null);
    }
  }

  const card: React.CSSProperties = {
    background: "white", borderRadius: 16, padding: 32,
    boxShadow: "0 4px 32px rgba(0,0,0,0.10)", maxWidth: 760, width: "100%", margin: "0 auto",
  };

  const finalStatus = responded || (d?.recipient_status === "signed" ? "signed" : d?.recipient_status === "declined" ? "declined" : null);
  const notMyTurn = !finalStatus && d?.recipient_status === "pending";
  const isExpired = d?.status === "expired";
  const canSign = readToEnd && !!typedName.trim() && consentChecked && !responding;

  return (
    <div style={{ minHeight: "100vh", background: "linear-gradient(135deg, #0f172a 0%, #1e3a8a 100%)", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <LoadingOverlay active={!!responding} />
      <div style={{ margin: "24px 0 32px", textAlign: "center" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, marginBottom: 8 }}>
          <div style={{ width: 32, height: 32, borderRadius: 8, background: "#0f766e", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <span style={{ fontSize: 12, fontWeight: 900, color: "#fff" }}>HR</span>
          </div>
          <span style={{ fontSize: 14, fontWeight: 700, color: "#f1f5f9" }}>{d?.company_name || "HRxAI"}</span>
        </div>
        <div style={{ fontSize: 13, color: "#94a3b8" }}>Document for Signature</div>
      </div>

      {isLoading && (
        <div style={card}><div style={{ textAlign: "center", padding: "32px 0", color: "#64748b" }}>Loading…</div></div>
      )}

      {!isLoading && isError && (
        <div style={card}>
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 40, marginBottom: 16 }}>⚠️</div>
            <h2 style={{ margin: "0 0 8px", color: "#1e3a8a", fontSize: 20 }}>Link Not Found</h2>
            <p style={{ margin: 0, color: "#64748b", fontSize: 14 }}>This signing link is invalid or no longer valid. Please contact the sender.</p>
          </div>
        </div>
      )}

      {!isLoading && !isError && finalStatus && (
        <div style={card}>
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>{finalStatus === "signed" ? "🎉" : "👋"}</div>
            <h2 style={{ margin: "0 0 8px", color: "#1e3a8a", fontSize: 22 }}>
              {finalStatus === "signed" ? "Signed!" : "Declined"}
            </h2>
            <p style={{ margin: "0 0 16px", color: "#64748b", fontSize: 14, lineHeight: 1.6 }}>
              {finalStatus === "signed"
                ? "Thank you — the sender has been notified."
                : "Thank you for letting us know. The sender has been notified."}
            </p>
          </div>
        </div>
      )}

      {!isLoading && !isError && d && !finalStatus && isExpired && (
        <div style={card}>
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 40, marginBottom: 16 }}>⏰</div>
            <h2 style={{ margin: "0 0 8px", color: "#1e3a8a", fontSize: 20 }}>Link Expired</h2>
            <p style={{ margin: 0, color: "#64748b", fontSize: 14 }}>This signing request has expired. Please contact the sender if you'd still like to proceed.</p>
          </div>
        </div>
      )}

      {!isLoading && !isError && d && !finalStatus && !isExpired && notMyTurn && (
        <div style={card}>
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 40, marginBottom: 16 }}>⏳</div>
            <h2 style={{ margin: "0 0 8px", color: "#1e3a8a", fontSize: 20 }}>Not Your Turn Yet</h2>
            <p style={{ margin: 0, color: "#64748b", fontSize: 14 }}>This document is waiting on an earlier signer. You'll receive an email once it's ready for you.</p>
          </div>
        </div>
      )}

      {!isLoading && !isError && d && !finalStatus && !isExpired && !notMyTurn && (
        <div style={card}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, marginBottom: 20, flexWrap: "wrap" }}>
            <div>
              <h2 style={{ margin: "0 0 4px", color: "#1e3a8a", fontSize: 20 }}>{d.title}</h2>
              <p style={{ margin: 0, color: "#64748b", fontSize: 13 }}>You've been asked to sign as <strong>{d.my_role}</strong>.</p>
            </div>
            <a href={pdfUrl} style={{ flexShrink: 0, padding: "8px 14px", borderRadius: 8, border: "1px solid #1e3a8a", color: "#1e3a8a", fontWeight: 600, fontSize: 12, textDecoration: "none" }}>
              ⬇ Download PDF
            </a>
          </div>

          <div style={{ marginBottom: 8 }}>
            <OfferPdfViewer url={pdfUrl} onReadToEnd={() => setReadToEnd(true)} />
          </div>
          {!readToEnd && (
            <p style={{ margin: "0 0 16px", fontSize: 12, color: "#94a3b8", textAlign: "center" }}>
              ↓ Scroll through the full document to enable your response
            </p>
          )}

          {!declining ? (
            <>
              <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 10, padding: "16px 18px", margin: "16px 0" }}>
                <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: "#475569", marginBottom: 6 }}>Sign</label>
                <input
                  value={typedName} onChange={e => setTypedName(e.target.value)}
                  placeholder="Type your full legal name"
                  style={{ width: "100%", padding: "10px 12px", borderRadius: 8, fontSize: 14, border: "1.5px solid #e2e8f0", outline: "none", color: "#1e293b", boxSizing: "border-box", marginBottom: 12 }}
                />
                <SignaturePad onChange={setSignatureDrawn} />
                <label style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 12, color: "#475569", marginTop: 12, cursor: "pointer" }}>
                  <input type="checkbox" checked={consentChecked} onChange={e => setConsentChecked(e.target.checked)} style={{ marginTop: 2 }} />
                  I agree that clicking below constitutes my electronic signature on this document.
                </label>
              </div>

              {error && (
                <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, padding: "10px 14px", color: "#dc2626", fontSize: 13, marginBottom: 16 }}>
                  {error}
                </div>
              )}

              <div style={{ display: "flex", gap: 12 }}>
                <button onClick={() => handle("sign")} disabled={!canSign}
                  style={{ flex: 1, padding: "14px 0", borderRadius: 10, border: "none", background: !canSign ? "#94a3b8" : "linear-gradient(135deg,#15803d,#22c55e)", color: "white", fontWeight: 700, fontSize: 15, cursor: !canSign ? "not-allowed" : "pointer" }}>
                  {responding === "sign" ? "Submitting…" : "✅ Sign"}
                </button>
                <button onClick={() => setDeclining(true)} disabled={!!responding || !readToEnd}
                  style={{ flex: 1, padding: "14px 0", borderRadius: 10, border: "2px solid #fecaca", background: "white", color: (!!responding || !readToEnd) ? "#fca5a5" : "#dc2626", fontWeight: 700, fontSize: 15, cursor: (!!responding || !readToEnd) ? "not-allowed" : "pointer" }}>
                  Decline
                </button>
              </div>
            </>
          ) : (
            <div>
              <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: "#475569", marginBottom: 6 }}>Reason for declining</label>
              <textarea value={declineReason} onChange={e => setDeclineReason(e.target.value)} rows={3}
                style={{ width: "100%", padding: "10px 12px", borderRadius: 8, fontSize: 13, border: "1.5px solid #e2e8f0", outline: "none", color: "#1e293b", boxSizing: "border-box" }} />
              {error && (
                <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, padding: "10px 14px", color: "#dc2626", fontSize: 13, margin: "12px 0" }}>
                  {error}
                </div>
              )}
              <div style={{ display: "flex", gap: 12, marginTop: 12 }}>
                <button onClick={() => handle("decline")} disabled={!declineReason.trim() || !!responding}
                  style={{ flex: 1, padding: "12px 0", borderRadius: 10, border: "none", background: !declineReason.trim() ? "#94a3b8" : "#dc2626", color: "white", fontWeight: 700, fontSize: 14, cursor: !declineReason.trim() ? "not-allowed" : "pointer" }}>
                  {responding === "decline" ? "Submitting…" : "Confirm Decline"}
                </button>
                <button onClick={() => setDeclining(false)} style={{ flex: 1, padding: "12px 0", borderRadius: 10, border: "1px solid #e2e8f0", background: "white", fontSize: 14, cursor: "pointer" }}>
                  Cancel
                </button>
              </div>
            </div>
          )}

          <p style={{ margin: "16px 0 0", fontSize: 11, color: "#94a3b8", textAlign: "center" }}>
            By signing, you confirm your agreement to this document. The sender will be notified immediately.
          </p>
        </div>
      )}
    </div>
  );
}
